import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from '../config/environment';

// 🔇 Toggle verbose logging
const ENABLE_VERBOSE_LOGS = false;
const log = (...args: any[]) => { if (ENABLE_VERBOSE_LOGS) console.log(...args); };

export interface TutorSubscription {
  id: string;
  tutorId: string;
  plan: string;
  billingSource: string;
  status: string;
  trialEndsAt?: string;
  subscriptionValidUntil?: string;
  foundingPriceUntil?: string;
  hasFoundingPrice?: boolean;
  daysRemaining?: number;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  createdAt: string;
  updatedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  enabledFeatures?: string[];
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  plan: string;
  status: string;
  subscription?: TutorSubscription | null;
}

/**
 * Get auth token from Cognito
 * Note: API Gateway Cognito authorizers require the ID token, not the access token
 */
async function getAuthToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw new Error('Authentication failed');
  }
}

/**
 * Get current subscription status via REST API
 */
export async function getMySubscription(): Promise<SubscriptionStatus> {
  log('📡 [subscription-rest-service] Calling REST API for subscription status...');
  
  const apiUrl = config.API_BASE_URL || 'https://brxtb3rm91.execute-api.ap-southeast-2.amazonaws.com/dev';
  const endpoint = `${apiUrl}/subscriptions/status`;
  
  try {
    const token = await getAuthToken();
    log('📡 [subscription-rest-service] Got auth token (length):', token.length);
    log('📡 [subscription-rest-service] Calling endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    log('📡 [subscription-rest-service] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [subscription-rest-service] API error:', response.status, errorText);
      throw new Error(`API request failed: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    log('✅ [subscription-rest-service] Success! Data:', responseData);
    
    // Unwrap the data from the API response wrapper
    return responseData.data;
  } catch (error) {
    console.error('❌ [subscription-rest-service] Error fetching subscription:', error);
    throw error;
  }
}

/**
 * Start trial subscription via REST API
 */
export async function startTrial(): Promise<TutorSubscription> {
  log('📡 [subscription-rest-service] Starting trial...');
  
  const apiUrl = config.API_BASE_URL || 'https://brxtb3rm91.execute-api.ap-southeast-2.amazonaws.com/dev';
  const endpoint = `${apiUrl}/trial/start`;
  
  try {
    const token = await getAuthToken();
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [subscription-rest-service] API error:', errorText);
      throw new Error(`Failed to start trial: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    log('✅ [subscription-rest-service] Trial started:', data);
    
    return data;
  } catch (error) {
    console.error('❌ [subscription-rest-service] Error starting trial:', error);
    throw error;
  }
}

/**
 * Cancel subscription via REST API
 */
export async function cancelSubscription(reason?: string): Promise<TutorSubscription> {
  const apiUrl = config.API_BASE_URL || 'https://brxtb3rm91.execute-api.ap-southeast-2.amazonaws.com/dev';
  const endpoint = `${apiUrl}/subscriptions/cancel`;
  
  try {
    const token = await getAuthToken();
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      throw new Error(`Failed to cancel subscription: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
}

/**
 * Create Stripe checkout session for upgrade
 */
export async function createCheckoutSession(plan: string): Promise<{ sessionUrl: string; sessionId: string; url: string }> {
  const apiUrl = config.API_BASE_URL || 'https://brxtb3rm91.execute-api.ap-southeast-2.amazonaws.com/dev';
  const endpoint = `${apiUrl}/subscriptions/create-checkout`;
  
  // Determine base URL for return URLs
  const baseUrl = window.location.origin;
  const successUrl = `${baseUrl}/subscription?success=true&session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${baseUrl}/subscription?canceled=true`;
  
  console.log('🛒 [subscription-rest-service] Creating checkout session:', {
    plan,
    successUrl,
    cancelUrl,
    baseUrl
  });
  
  try {
    const token = await getAuthToken();
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        plan,
        successUrl,
        cancelUrl
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [subscription-rest-service] Checkout error:', response.status, errorText);
      throw new Error(`Failed to create checkout session: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ [subscription-rest-service] Checkout session created:', result);
    
    // Return both sessionUrl (for backwards compat) and the actual response
    return {
      sessionUrl: result.data?.url || result.url,
      sessionId: result.data?.sessionId || result.sessionId,
      url: result.data?.url || result.url
    };
  } catch (error) {
    console.error('❌ [subscription-rest-service] Error creating checkout session:', error);
    throw error;
  }
}

/**
 * Get Stripe customer portal URL
 */
export async function getCustomerPortalUrl(): Promise<{ portalUrl: string }> {
  const apiUrl = config.API_BASE_URL || 'https://brxtb3rm91.execute-api.ap-southeast-2.amazonaws.com/dev';
  const endpoint = `${apiUrl}/subscriptions/portal`;
  
  console.log('🔗 [subscription-rest-service] Fetching customer portal URL from:', endpoint);
  
  try {
    const token = await getAuthToken();
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    console.log('📡 [subscription-rest-service] Portal response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ [subscription-rest-service] Portal error response:', errorData);
      throw new Error(`Failed to get customer portal URL: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ [subscription-rest-service] Portal response:', result);
    
    // Extract portalUrl from the nested response structure
    const portalUrl = result.data?.portalUrl || result.portalUrl;
    
    if (!portalUrl) {
      console.error('❌ [subscription-rest-service] No portalUrl in response:', result);
      throw new Error('No portal URL returned from server');
    }
    
    return { portalUrl };
  } catch (error) {
    console.error('❌ [subscription-rest-service] Error getting customer portal URL:', error);
    throw error;
  }
}

// Helper functions (same as before)
export function formatDaysRemaining(days: number | undefined | null): string {
  if (days === undefined || days === null) return 'N/A';
  if (days === 0) return 'Expires today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}

export function formatPlanName(plan?: string | null): string {
  if (!plan) return 'No Plan';
  
  switch (plan) {
    case 'trial': return 'Free Trial';
    case 'pro': return 'Pro Plan';
    case 'studio': return 'Studio Plan';
    default: return 'No Plan';
  }
}

export function getPlanPrice(plan?: string | null, hasFoundingPrice: boolean = false): string {
  if (!plan) return '';
  
  switch (plan) {
    case 'trial': return 'Free';
    case 'pro': return hasFoundingPrice ? 'AUD $19/mo (founding price)' : 'AUD $29/mo';
    case 'studio': return hasFoundingPrice ? 'AUD $49/mo (founding price)' : 'AUD $79/mo';
    default: return '';
  }
}

